import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { apiService } from '../services/api';
import { SharedWorkflowDetail, RunInstance, RunResult } from '../types';
import SharedProjectBanner from '../components/social/SharedProjectBanner';
import SharedRunButton from '../components/social/SharedRunButton';
import SharedRunOutputPanel from '../components/social/SharedRunOutputPanel';
import SharedRunInputDialog from '../components/social/SharedRunInputDialog';
import SharedFlowVisualization from '../components/social/SharedFlowVisualization';
import { useContextualNavigation } from '../hooks/useContextualNavigation';

interface OperationInfo {
  project_id: number;
  name: string;
  owner_username: string;
  owner_user_id: number;
  view_count: number;
}

const SharedWorkflowViewPage: React.FC = () => {
  const { projectId, workflowId } = useParams<{
    projectId: string;
    workflowId: string;
  }>();
  const { getBackNavigation } = useContextualNavigation();
  const backNav = getBackNavigation(
    `/shared/projects/${projectId}`,
    'Back to Project'
  );

  const [workflow, setWorkflow] = useState<SharedWorkflowDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operationInfo, setOperationInfo] = useState<OperationInfo | null>(null);

  // Run state
  const [isRunning, setIsRunning] = useState(false);
  const [runInstance, setRunInstance] = useState<RunInstance | null>(null);
  const [runResults, setRunResults] = useState<RunResult[]>([]);
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [showInputDialog, setShowInputDialog] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const numericOperationId = projectId ? parseInt(projectId, 10) : 0;
  const numericWorkflowId = workflowId ? parseInt(workflowId, 10) : 0;

  useEffect(() => {
    loadData();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [numericOperationId, numericWorkflowId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [orchData, projData] = await Promise.all([
        apiService.getSharedWorkflow(numericOperationId, numericWorkflowId),
        apiService.getSharedProject(numericOperationId),
      ]);
      setWorkflow(orchData);
      setOperationInfo({
        project_id: projData.project_id,
        name: projData.name,
        owner_username: projData.owner_username,
        owner_user_id: projData.owner_user_id || 0,
        view_count: projData.view_count || 0,
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Not found';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const effectiveOperationId = numericOperationId || operationInfo?.project_id || 0;
  const effectiveWorkflowId =
    numericWorkflowId || (workflow?.orchestration_id ? Number(workflow.orchestration_id) : 0);

  const userPromptStep = workflow?.steps?.find(
    (step) => step.step_type === 'user_prompt'
  );
  const hasUserPromptStep = !!userPromptStep;

  const executeRun = async (inputMessage?: string) => {
    setIsRunning(true);
    setRunError(null);
    try {
      const instance = await apiService.runSharedWorkflow(
        effectiveOperationId,
        effectiveWorkflowId,
        inputMessage
      );
      setRunInstance(instance);
      setRunResults([]);

      const runId = instance.run_instance_id;

      // Check if run already completed (e.g., fast execution)
      if (instance.status === 'completed') {
        setRunStatus('completed');
        setIsRunning(false);
        try {
          const results = await apiService.getRunResults(runId);
          setRunResults(results);
        } catch {
          // Results may not be available
        }
        return;
      }

      setRunStatus('running');

      pollingRef.current = setInterval(async () => {
        try {
          const updatedInstance = await apiService.getRunInstance(runId);
          if (updatedInstance.status === 'completed' || updatedInstance.status === 'failed') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setRunStatus(updatedInstance.status);
            setIsRunning(false);

            if (updatedInstance.status === 'completed') {
              try {
                const results = await apiService.getRunResults(runId);
                setRunResults(results);
              } catch {
                // Results may not be available
              }
            }
          }
        } catch {
          // Polling error, continue
        }
      }, 2000);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail || err?.message || 'Failed to start run';
      setRunError(errorMessage);
      setRunStatus('error');
      setIsRunning(false);
      // Close dialog on error so user can see the error and retry
      setShowInputDialog(false);
    }
  };

  const handleRunClick = () => {
    if (hasUserPromptStep) {
      setShowInputDialog(true);
      return;
    }
    executeRun(undefined);
  };

  const handleInputSubmit = (inputMessage: string) => {
    setShowInputDialog(false);
    executeRun(inputMessage);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading workflow...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Workflow not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Back Navigation */}
      <Link
        to={backNav.path}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        {backNav.label}
      </Link>

      {/* Shared Banner */}
      {operationInfo && (
        <div className="mb-4">
          <SharedProjectBanner
            ownerUsername={operationInfo.owner_username}
            ownerUserId={operationInfo.owner_user_id}
            viewCount={operationInfo.view_count}
            projectName={operationInfo.name}
          />
        </div>
      )}

      {/* Header with Run Button */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {workflow.name}
          </h1>
          {workflow.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-2">{workflow.description}</p>
          )}
          {operationInfo && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Shared by @{operationInfo.owner_username}
            </p>
          )}
          <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300">
            Read Only
          </span>
        </div>
        <SharedRunButton
          onClick={handleRunClick}
          isRunning={isRunning}
          visible={!showInputDialog}
        />
      </div>

      {/* Input Dialog */}
      <SharedRunInputDialog
        isOpen={showInputDialog}
        onClose={() => setShowInputDialog(false)}
        onSubmit={handleInputSubmit}
        workflowName={workflow.name}
        stepLabel={userPromptStep?.label ?? undefined}
        stepDescription={userPromptStep?.description ?? undefined}
        stepInputMessage={userPromptStep?.input_message ?? undefined}
      />

      {/* Run Output */}
      <SharedRunOutputPanel
        runStatus={runStatus}
        runResults={runResults}
        runError={runError}
      />

      {/* Flow Visualization */}
      {workflow.steps.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Workflow
          </h2>
          <div
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            style={{ height: Math.min(200 + workflow.steps.length * 80, 500) }}
          >
            <SharedFlowVisualization
              steps={workflow.steps}
              className="h-full"
            />
          </div>
        </div>
      )}

      {/* Steps */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Steps ({workflow.steps.length})
        </h2>
        {workflow.steps.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No steps defined.</p>
        ) : (
          <div className="space-y-3">
            {workflow.steps.map((step) => (
              <div
                key={step.orchestration_step_id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                    {step.step_type || 'step'}
                  </span>
                  {step.label && (
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {step.label}
                    </span>
                  )}
                </div>
                {step.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {step.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedWorkflowViewPage;
